import { format, intervalToDuration } from "date-fns";
import { CalendarIcon, VideoCameraIcon } from "@heroicons/react/20/solid";
import type { AssociatedObject } from "./types";
import { isMeetingMetadata } from "./types";

interface MeetingCardProps {
  meeting: AssociatedObject;
}

const MeetingCard = ({ meeting }: MeetingCardProps) => {
  if (!isMeetingMetadata(meeting.meta_data)) {
    return null;
  }

  const metadata = meeting.meta_data;
  const {
    title,
    organizer,
    attendees,
    start_time: startTime,
    end_time: endTime,
    description,
    meeting_platform: meetingPlatform,
    meeting_url: meetingUrl,
    recording_available: recordingAvailable,
    location,
  } = metadata;

  const startDate = startTime ? new Date(startTime) : null;
  const endDate = endTime ? new Date(endTime) : null;
  const isUpcoming = startDate && startDate > new Date();

  const formattedDate = startDate ? format(startDate, "MMM d, yyyy") : "";
  const formattedTime = startDate ? format(startDate, "h:mm a") : "";

  let durationText = "";
  if (startDate && endDate) {
    const duration = intervalToDuration({ start: startDate, end: endDate });
    const parts: string[] = [];
    if (duration.hours) parts.push(`${duration.hours}h`);
    if (duration.minutes) parts.push(`${duration.minutes}m`);
    durationText = parts.join(" ");
  }

  const getPlatformBadge = (platform?: string) => {
    if (!platform) return null;

    const badgeConfig: Record<string, { bg: string; text: string; label: string }> = {
      zoom: { bg: "bg-blue-100", text: "text-blue-800", label: "Zoom" },
      teams: { bg: "bg-purple-100", text: "text-purple-800", label: "Teams" },
      meet: { bg: "bg-green-100", text: "text-green-800", label: "Google Meet" },
      "google-meet": { bg: "bg-green-100", text: "text-green-800", label: "Google Meet" },
    };

    const config = badgeConfig[platform.toLowerCase()] || {
      bg: "bg-gray-100",
      text: "text-gray-800",
      label: platform,
    };

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-grow min-w-0">
          <h3 className="text-base font-semibold text-gray-900 truncate">
            {title || "(No Title)"}
          </h3>
        </div>
        <div className="flex-shrink-0">
          <CalendarIcon className="h-5 w-5 text-gray-400" />
        </div>
      </div>

      {/* Date and Time */}
      <div className="text-sm text-gray-600 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium">{formattedDate}</span>
          {formattedTime && (
            <>
              <span className="text-gray-400">•</span>
              <span>{formattedTime}</span>
            </>
          )}
          {durationText && (
            <>
              <span className="text-gray-400">•</span>
              <span>{durationText}</span>
            </>
          )}
        </div>
        {location && (
          <div className="text-xs text-gray-500 mt-1">{location}</div>
        )}
      </div>

      {/* Platform and Status Badges */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {meetingPlatform && getPlatformBadge(meetingPlatform)}
        {isUpcoming && (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
            Upcoming
          </span>
        )}
        {recordingAvailable && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
            <VideoCameraIcon className="h-3 w-3" />
            Recording
          </span>
        )}
      </div>

      {/* Description */}
      {description && (
        <div className="text-sm text-gray-700 mb-3 line-clamp-2">
          {description}
        </div>
      )}

      {/* Organizer */}
      {organizer && (
        <div className="text-sm text-gray-600 mb-3">
          <span className="text-xs text-gray-500 font-medium">Organizer: </span>
          <span>{organizer}</span>
        </div>
      )}

      {/* Participants */}
      {attendees && attendees.length > 0 && (
        <div className="border-t border-gray-100 pt-3 mt-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 font-medium">Participants:</span>
            <div className="flex flex-wrap gap-2">
              {attendees.slice(0, 3).map((attendee) => (
                <div key={attendee} className="flex items-center gap-1">
                  <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-xs font-medium text-white">
                    {attendee.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-xs text-gray-700 truncate max-w-[120px]">
                    {attendee}
                  </span>
                </div>
              ))}
              {attendees.length > 3 && (
                <span className="text-xs text-gray-500">
                  +{attendees.length - 3} more
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Join Button */}
      {isUpcoming && meetingUrl && (
        <div className="mt-4">
          <a
            href={meetingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Join Meeting
          </a>
        </div>
      )}
    </div>
  );
};

export default MeetingCard;
